import { useState, Fragment, SyntheticEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as log from 'loglevel';
import styled from 'styled-components';
import i18n from 'src/utils/translate';
import api from 'src/utils/api';
import linkUtils from 'src/utils/linkUtils';
import { setAlert } from 'src/typescript/components/alert/actions';
import { hideRespondImageUpload } from 'src/constants/restrictedCommunities';
import { State } from 'src/types';
import { useIsPoll } from 'src/utils/hooks';

type RespondImageUploadProps = {
  uid: string,
  onUploadSuccess: (imageHash: string, imageUrl: string) => void,
};

export default function RespondImageUpload({uid = 'image-upload-reply', onUploadSuccess}: RespondImageUploadProps) {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);

  const isHidden = useHideRespondImageUpload();

  if (isHidden) return null;

  const onChange = (e: SyntheticEvent) => {
    const target = e.target as HTMLInputElement;
    const currentFiles = target.files; // FileList object (similar to NodeList)
    if (!currentFiles || currentFiles && currentFiles.length === 0) return;
    onUpload(currentFiles[0]);
  };

  const onUpload = async (file: Blob) => {
    try {
      setIsLoading(true);
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post('private/images/response', formData);
      const { data: { imageHash }, status } = response;
      setIsLoading(false);
      if (status === 200 && imageHash) {
        onUploadSuccess(imageHash, linkUtils.generateReplyPreviewImageUrlFromHash(imageHash));
      } else {
        dispatch(setAlert(i18n.t('Whoops! Looks like there was a problem uploading your image.')));
      }
    } catch (ex) {
      log.error(ex);
      if (ex.message.includes('413')) {
        dispatch(setAlert(i18n.t('Whoops! Looks like your file exceeds our 10MB limit.')));
      } else {
        dispatch(setAlert(i18n.t('Whoops! Looks like there was a problem uploading your image.')));
      }
      setIsLoading(false);
    }
  };

  return (
    <Container>
      {isLoading
      ? <LoadingSpinner />
      : (
        <Fragment>
          <UploadButton aria-label="Add image" htmlFor={uid}>
            <span className="icon icon-picture" title="picture" />
          </UploadButton>
          <UploadInput
            accept="image/*"
            id={uid}
            type="file"
            onChange={onChange}
          />
        </Fragment>
      )}
    </Container>
  );
}

const useHideRespondImageUpload = () => {
  const slug = useSelector(({context}: State) => context.currentCommunity?.slug);
  const isPoll = useIsPoll();

  if (!slug) return false;
  return hideRespondImageUpload.includes(slug) || isPoll;
};

const Container = styled.div`
  display: inline-block;
  padding: 0 10px;

  @media (${({theme}) => theme.underScreenSmall}) {
    padding: 0 9px;
  }
`;

const UploadInput = styled.input`
  position: absolute;
  left: -999em;
  opacity: 0;
`;

const UploadButton = styled.label`
  margin: 0;
  padding-right: 2px;
  color: ${({theme}) => theme.colorGreyDark};
  font-size: 23px;
  line-height: 45px;
  cursor: pointer;

  &:hover, &.active {
    color: ${({theme}) => theme.colorBlue};
  }
`;

const LoadingSpinner = styled.div`
  position: relative;
  width: 25px;
  height: 100%;

  ::after {
    content: '';
    position: absolute;
    left: 50%;
    width: 46px;
    height: 46px;
    background-image: url('https://images.hu-production.be/static/img/green-spinner.svg');
    background-position: center;
    background-size: cover;
    transform: translate(-50%, 0);

    @media (max-width: 450px) {
      top: 50%;
      transform: translate(-50%, -50%);
    }
  }
`;
